/**
 * Affiliate offer config — drives the <AffiliateRecommendation> block that renders
 * after the tool result (see components/ToolLayout.tsx, components/AffiliateRecommendation.tsx).
 *
 * Lookup order in the component: offers for the exact `toolSlug` first, then the
 * tool's `category` slug as a fallback. So high-traffic pages get a precisely-matched
 * offer (e.g. hash-lookup -> antivirus) while everything else in a category gets a
 * sensible default.
 *
 * HOW TO GO LIVE: an offer only renders once its `affiliateLink` is set. Ship this
 * file with `affiliateLink: ''` and the block stays hidden — no broken links, and we
 * never send free (un-commissioned) referrals. Paste the real tracking link from the
 * network (Impact / PartnerStack / CJ) into `affiliateLink` and it goes live on the
 * next deploy. `homepage` is reference-only (the merchant's normal URL).
 *
 * Category slugs come from lib/data/tools.ts `toolCategories`:
 *   security, developer, network, media, compliance, assessment, comparison, ai, planning
 *
 * NOTE: compliance / assessment categories are intentionally NOT wired here. Those pages
 * (e.g. cybersecurity-maturity-assessment) carry "we recommend the best solutions, not
 * what earns us commissions" trust copy — wiring affiliate offers there is a brand
 * decision to make deliberately, not a default.
 */

export interface AffiliateOffer {
  /** Stable key for analytics + dedupe. */
  slug: string;
  /** Brand name shown on the CTA card. */
  name: string;
  /** One-line, honest pitch tied to what the tool just did. */
  blurb: string;
  /** Button label, e.g. "Scan with Malwarebytes". */
  cta: string;
  /** The network tracking link. EMPTY = offer hidden until approved + pasted. */
  affiliateLink: string;
  /** Merchant's normal URL — reference only, never rendered as the click target. */
  homepage: string;
  /** Optional small badge, e.g. "Editor's pick". */
  badge?: string;
}

/**
 * Keyed by toolSlug (specific) OR category slug (fallback). The component prefers a
 * toolSlug match, then falls back to the category.
 */
export const AFFILIATE_OFFERS: Record<string, AffiliateOffer[]> = {
  // ---- per-tool, highest intent (paste affiliateLink to activate) ----
  'hash-lookup': [
    {
      slug: 'malwarebytes',
      name: 'Malwarebytes',
      blurb: 'This checks one hash. Scan your whole system for the malware these signatures came from.',
      cta: 'Scan with Malwarebytes',
      affiliateLink: '', // CJ / direct (Malwarebytes affiliate)
      homepage: 'https://www.malwarebytes.com/',
    },
  ],
  'file-magic-number-checker': [
    {
      slug: 'malwarebytes',
      name: 'Malwarebytes',
      blurb: 'Suspicious file? Run a full malware scan to be sure it is safe to open.',
      cta: 'Scan with Malwarebytes',
      affiliateLink: '',
      homepage: 'https://www.malwarebytes.com/',
    },
  ],
  'ip-risk-checker': [
    {
      slug: 'nordvpn',
      name: 'NordVPN',
      blurb: 'Hide your own IP and block malicious hosts before they reach you.',
      cta: 'Get NordVPN',
      affiliateLink: '', // Impact (NordVPN)
      homepage: 'https://nordvpn.com/',
    },
  ],
  'password-strength-checker': [
    {
      slug: '1password',
      name: '1Password',
      blurb: 'Stop guessing at strength — generate and store unguessable passwords on every device.',
      cta: 'Try 1Password',
      affiliateLink: '', // Impact / CJ (1Password)
      homepage: 'https://1password.com/',
      badge: 'Editor’s pick',
    },
  ],
  'breach-checker': [
    {
      slug: 'aura',
      name: 'Aura',
      blurb: 'Found in a breach? Monitor the dark web for your leaked data and lock down identity theft.',
      cta: 'Check with Aura',
      affiliateLink: '', // Impact (Aura)
      homepage: 'https://www.aura.com/',
    },
  ],
  'ssh-key-generator': [
    {
      slug: 'digitalocean',
      name: 'DigitalOcean',
      blurb: 'Got a key — now you need a box. Spin up a secure cloud server in under a minute.',
      cta: 'Try DigitalOcean',
      affiliateLink: '', // Impact (DigitalOcean)
      homepage: 'https://www.digitalocean.com/',
    },
  ],

  // ---- per-category fallbacks (lower intent, broad fit) ----
  security: [
    {
      slug: 'nordvpn',
      name: 'NordVPN',
      blurb: 'Encrypt your connection and block trackers, malware, and malicious sites.',
      cta: 'Get NordVPN',
      affiliateLink: '',
      homepage: 'https://nordvpn.com/',
    },
  ],
  developer: [
    {
      slug: 'digitalocean',
      name: 'DigitalOcean',
      blurb: 'Deploy your project to a secure, developer-friendly cloud in minutes.',
      cta: 'Try DigitalOcean',
      affiliateLink: '',
      homepage: 'https://www.digitalocean.com/',
    },
  ],
  network: [
    {
      slug: 'nordvpn',
      name: 'NordVPN',
      blurb: 'Secure your network traffic on any connection with a fast, audited VPN.',
      cta: 'Get NordVPN',
      affiliateLink: '',
      homepage: 'https://nordvpn.com/',
    },
  ],
};

/** Returns the offers for a tool, preferring an exact toolSlug match over the category. */
export function getAffiliateOffers(
  toolSlug: string,
  category: string
): AffiliateOffer[] {
  const byTool = AFFILIATE_OFFERS[toolSlug];
  const offers = byTool ?? AFFILIATE_OFFERS[category] ?? [];
  // Only surface offers that have a real tracking link — never ship a placeholder.
  return offers.filter((o) => o.affiliateLink.trim().length > 0);
}
